import React, {useMemo} from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import {Vega} from 'react-vega';
import {ADDITIONALITY, CALCULATIONS, CO2_LABELS} from '../../../../config';
import {getLabelById} from '../../../../utils/getLabelById';

const BiomasCO2Chart = ({minChartYear, maxChartYear, firstCalculation, finalCalculation, selectedCo2Id, showFirstCalculation, showAdditionality, isMonitoring}) => {

  const selectedCo2Label = useMemo(() => getLabelById(selectedCo2Id, CO2_LABELS), [selectedCo2Id]);
  const hasFinalCalculation = useMemo(() => finalCalculation.length >= 1, [finalCalculation]);

  const additionalityCalculation = firstCalculation && finalCalculation &&
    firstCalculation.map(firstItem => {

      const matchingItem = finalCalculation.find(finalItem => finalItem.year === firstItem.year && finalItem.co2 === firstItem.co2);

      if(matchingItem){
        return {
          year: firstItem.year,
          value: parseFloat(matchingItem.value)-parseFloat(firstItem.value),
          co2: firstItem.co2,
          type: getLabelById(ADDITIONALITY, CALCULATIONS)
        };
      } return null;

    });

  const finalCalculationHasOneYear = useMemo(() => {
    if (!finalCalculation) return false;
    const uniqueYears = new Set(finalCalculation.map(d => d.year));
    return uniqueYears.size === 1;
  }, [finalCalculation]);

  const allCalculations = [...firstCalculation, ...finalCalculation, ...additionalityCalculation].filter(Boolean);

  const getCalculationsToShow = () => {

    if(!isMonitoring) {
      return allCalculations.filter(c => c.type !== getLabelById(ADDITIONALITY, CALCULATIONS));
    } else {
      const showAll = showFirstCalculation && showAdditionality && hasFinalCalculation;
      const showOnlyFinalCalculation = !showFirstCalculation && !showAdditionality && hasFinalCalculation;
      const showFinalAdditionalCalculation = !showFirstCalculation && showAdditionality && hasFinalCalculation;
      const showFirstAndFinalCalculation = showFirstCalculation && !showAdditionality && hasFinalCalculation;
      const showNothing = !showFirstCalculation && !showAdditionality && !hasFinalCalculation;
      if (showAll) {
        return allCalculations;
      }

      if (showOnlyFinalCalculation) {
        return [...finalCalculation].filter(Boolean);
      }

      if (showFinalAdditionalCalculation) {
        return [...finalCalculation, ...additionalityCalculation].filter(Boolean);
      }

      if (showFirstAndFinalCalculation) {
        return [...firstCalculation, ...finalCalculation];
      }

      if (showNothing) {
        return [];
      }
    
      return [...firstCalculation];
    }
  };
  
  const calculationsToShow = useMemo(getCalculationsToShow, [showFirstCalculation, showAdditionality, firstCalculation, finalCalculation]);

  const co2ToShow = useMemo(() => calculationsToShow
    .filter(calculation => calculation.co2 === selectedCo2Label), [selectedCo2Label, calculationsToShow]);
  
  const domain =  CALCULATIONS.map(d => d.label);
  const rangeColor = (domain.map(d => CALCULATIONS.filter(c => c.label === d)[0].color));

  const allValues = isMonitoring 
    ?  [...new Set(allCalculations.map(d => d.value))]
    : [...new Set([...firstCalculation, ...finalCalculation].map(d => d.value))];

  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);

  const baselineDash = [10, 5];
  const projectDash = isMonitoring ? [8, 5] : [1, 0];
  const additionalityDash = [1, 0];
  const monitoringDash = [1, 0];
  const rangeDash = [baselineDash, projectDash, additionalityDash, monitoringDash];

  const spec= useMemo (() => ({
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    width: 700, height: 300,
    data: {
      name: 'co2',
      values: co2ToShow
    },
    layer: [
      {
        mark: {type: 'line'},
        opacity: isMonitoring && finalCalculationHasOneYear && !showFirstCalculation ? 0 : 1, filled: true,
        encoding: {
          x: {
            field: 'year',
            type: 'ordinal',
            title: null,
            axis: {labelAngle: 90, grid: true},
            scale: {
              domain: Array.from({length: maxChartYear - minChartYear + 1}, (_, i) => minChartYear + i)
            },
          },
          y: {
            field: 'value',
            type: 'quantitative',
            title: null,
            axis: {labelAngle: 0, grid: false},
            scale: {
              domain: [minValue, maxValue]
            }
          },
          color: {
            field: 'type',
            scale: {
              domain,
              range: rangeColor
            },
            type: 'nominal',
            title: 'Categoría de CO2',
            legend: null
          },
          strokeDash: {
            field: 'type',
            type: 'nominal',
            title: 'Tipo',
            legend: null,
            scale: {
              domain,
              range: rangeDash
            }
          },
          tooltip: [
            {field: 'year', type: 'ordinal', title: 'Año'},
            {field: 'value', type: 'quantitative', title: 'Valor'},
            {field: 'co2', type: 'nominal', title: 'Categoría de CO2'},
            {field: 'type', type: 'nominal', title: 'Tipo'}
          ]
        },
      },
      {
        mark: {type: 'point', opacity: isMonitoring && finalCalculationHasOneYear ? 1 : 0, filled: true },
        transform: [{ filter: 'datum.type !== \'Proyecto Selvícola\''} ],
        encoding: {
          x: {
            field: 'year',
            type: 'ordinal',
            title: null,
            axis: {labelAngle: 90, grid: true},
            scale: {
              domain: Array.from({length: maxChartYear - minChartYear + 1}, (_, i) => minChartYear + i)
            },
          },
          y: {
            field: 'value',
            type: 'quantitative',
            title: null,
            axis: {labelAngle: 0, grid: false},
            scale: {
              domain: [minValue, maxValue]
            }
          },
          size: { value: 100 },
          color: {
            field: 'type',
            scale: {
              domain,
              range: rangeColor
            },
            type: 'nominal',
            title: 'Categoría de CO2',
            legend: null
          },
          tooltip: [
            {field: 'year', type: 'ordinal', title: 'Año'},
            {field: 'value', type: 'quantitative', title: 'Valor'},
            {field: 'co2', type: 'nominal', title: 'Categoría de CO2'},
            {field: 'type', type: 'nominal', title: 'Tipo'}
          ]
        },
      }
    ]
  }), [co2ToShow, showAdditionality, finalCalculationHasOneYear]);

  return <Box mt={0.5} sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1}}>
    <Vega spec={spec} actions={false} onError={(err) => console.error('VEGA ERROR', err)}/>
  </Box>;
};

BiomasCO2Chart.propTypes = {
  minChartYear: PropTypes.number.isRequired,
  maxChartYear: PropTypes.number.isRequired,
  isMonitoring: PropTypes.bool.isRequired,
  showAdditionality: PropTypes.bool.isRequired,
  showFirstCalculation: PropTypes.bool.isRequired,
  firstCalculation: PropTypes.array,
  finalCalculation: PropTypes.array,
  selectedCo2Id: PropTypes.string.isRequired
};

export default BiomasCO2Chart;